import { useEffect, useState } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getCookieByName } from '@99designs/browser';
import { Box } from '@99designs/emu';
import {
    ProductDetailsContainer,
    getDesignInspiration,
    getPageConfigFromContentful,
    getProductGroupConfigByKey,
    getProductsByProductGroupName,
    getRelatedProductsByProductGroupKey,
    isValidProductGroupKey,
} from '@99designs/feature-product-details';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { createSSRApolloClient } from '@99designs/graph-utils';
import { Site } from '@99designs/site-navigation/lib/Site';
import DefaultPageLayout from '../../../components/common/DefaultPageLayout';
import { Head } from '../../../components/common/Head/';
import { fetchPromoBar } from '../../../components/common/PromoBar';
import { getContentfulClient } from '../../../lib/getContentfulClient';
import { getDefaultPageProps } from '../../../lib/getDefaultPageProps';
import { getFlashSaleConfig } from '../../../lib/getFlashSale';
import { HOUR_IN_SECONDS } from '../../../lib/time';
import type { Design } from '@99designs/feature-design-inspiration';
import type {
    ContestProduct,
    FixedPriceProjectProduct,
    ProductDetailsInspirationQuery,
    ProductGroup,
    ProductGroupKey,
    ProductGroupName,
    RelatedProduct,
} from '@99designs/feature-product-details';
import type { CurrencyCode } from '@99designs/i18n';
import type { Role } from '@99designs/site-navigation/consts';
import type { SiteConfig } from '@99designs/site-navigation/lib/Site';
import type { PromoBarContentType } from '../../../components/common/PromoBar';

function ProductDetailsPage({
    host,
    siteConfig,
    products,
    relatedProducts,
    designInspiration,
    userRole,
    productGroupKey,
    productGroup,
    flashSaleConfig,
    locale,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const currency = site.siteConfig['currency'] as CurrencyCode;
    const [promoBar, setPromoBar] = useState<PromoBarContentType | null>(null);
    const [promotionCoupon, setPromotionCoupon] = useState<string | null>(null);

    useEffect(() => {
        fetchPromoBar(productGroupKey, host).then((data) => {
            setPromoBar(data);
        });
    }, [productGroupKey, host]);

    useEffect(() => {
        const fetchPromotionCoupon = async () => {
            const response = await fetch('/start/xhr/coupon');
            if (response.ok) {
                const data = await response.json();
                if (data && data.promotion) {
                    setPromotionCoupon(data.promotion.campaign);
                }
            }
        };
        fetchPromotionCoupon();
    }, []);

    const isFlashSaleActive = flashSaleConfig === promotionCoupon;

    return (
        <DefaultPageLayout role={userRole} site={site} promoBar={promoBar}>
            <Head
                title={formatMessage(__('{productGroupTitle} details'), {
                    productGroupTitle: productGroup.attributes.title || products[0].title || '',
                })}
                description={productGroup.attributes.description || products[0].description || ''}
                robots="noindex"
                currentDomain={site.getDomain()}
            >
                <meta property="og:image" content={productGroup.attributes.hero[0].src} />
            </Head>
            <Box paddingTop={['2bu', '3bu', '3bu', '3bu']}>
                <ProductDetailsContainer
                    productGroup={productGroup}
                    productGroupKey={productGroupKey}
                    products={products}
                    designInspiration={designInspiration}
                    relatedProducts={relatedProducts}
                    currency={currency}
                    locale={locale}
                    isFlashSaleActive={isFlashSaleActive}
                />
            </Box>
        </DefaultPageLayout>
    );
}

type ServerSideProps = {
    host: string;
    products: ContestProduct[] | FixedPriceProjectProduct[];
    relatedProducts: RelatedProduct[] | null; // specifying this in order to enforce null checking
    designInspiration: Design[] | null; // specifying this in order to enforce null checking
    productGroupKey: ProductGroupKey;
    siteConfig: string;
    userRole: Role;
    productGroup: ProductGroup;
    isFlashSaleActive?: boolean;
    flashSaleConfig: string | null;
    locale: string;
};

// TODO: Getting around type checking to address some noisy monitors:
// https://vistaprint.slack.com/archives/C041ZRTUQR2/p1672171082383019
// Clean up once we are back from the xmas break.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ServerSidePropsWithError = Record<string, any>;

export const getServerSideProps: GetServerSideProps<
    ServerSideProps | ServerSidePropsWithError
> = async (context) => {
    const { req, res, query } = context;
    const { urlSlug } = query;
    const contentfulLanguage = context.locale?.slice(0, 2) || 'en';
    const locale = context.locale || 'en-US';

    if (process.env.USE_CSRF_CHECK) {
        const csrfToken = getCookieByName('_99csrf', req.headers['cookie'] || '');
        if (!csrfToken) {
            res.statusCode = 401;
            res.end();
            return {
                props: {},
            };
        }
    }

    if (!isValidProductGroupKey(urlSlug as string)) {
        return {
            notFound: true,
        };
    }

    const productGroupConfig = getProductGroupConfigByKey(urlSlug as ProductGroupKey);

    const client = createSSRApolloClient(req);
    const contentfulClient = getContentfulClient();

    const [
        defaultPageProps,
        productGroupAttributes,
        products,
        relatedProducts,
        designInspiration,
        flashSaleConfig,
    ] = await Promise.all([
        getDefaultPageProps(context),
        getPageConfigFromContentful(
            productGroupConfig.name as ProductGroupName,
            contentfulClient,
            contentfulLanguage
        ),
        // Call GraphQL here in order to make SSR work
        getProductsByProductGroupName(client, productGroupConfig.name as ProductGroupName),
        getRelatedProductsByProductGroupKey(client, urlSlug as ProductGroupKey),
        getDesignInspiration(client, {
            categories: [productGroupConfig.designInspirationCategoryKey],
            designerLevels:
                urlSlug === 'logo-essential' || urlSlug === 'logo-essential-and-consultation'
                    ? ['ENTRY']
                    : [],
        } as ProductDetailsInspirationQuery),
        getFlashSaleConfig(contentfulClient),
    ]);

    const productGroup = {
        ...productGroupConfig,
        ...productGroupAttributes,
    };

    if (!productGroup.attributes.active && query.preview !== 'true') {
        return {
            notFound: true,
        };
    }

    context.res.setHeader('Surrogate-Key', 'product-details');
    context.res.setHeader(
        'Surrogate-Control',
        `max-age=${HOUR_IN_SECONDS}, stale-while-revalidate=${HOUR_IN_SECONDS}`
    );

    return {
        props: {
            ...defaultPageProps,
            products,
            relatedProducts,
            designInspiration,
            productGroupKey: urlSlug as ProductGroupKey,
            productGroup,
            flashSaleConfig,
            locale,
        },
    };
};

export default ProductDetailsPage;
